import { FC } from "react";
import { AddIcon, Button, Input, Numeric, Select, Switch, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import Icon from "../../../../components/ui/Icon";
import useI18n from "../../../../hooks/useTranslations";
import {
  RESISTANCE_PARAMS,
  ResistanceParam,
  SUBSTRATE_CATEGORIES,
  SubstrateCategory,
} from "../../../../http/screwConnector/types/common";
import { ResistanceValue } from "../../../../http/screwConnector/types/screwDiameters";

const emptyRow = (): ResistanceValue => ({
  resistanceParam: "fax_k",
  substrateCategory: "solidwood_softwood",
  substrateSpecies: "",
  preDrilled: false,
  valueNMm2: null,
  referenceDensity: null,
  densityRange: null,
});

interface ResistanceValuesEditorProps {
  value: ResistanceValue[];
  onChange: (value: ResistanceValue[]) => void;
}

/**
 * resistanceValues can hold 10-20 rows per screw diameter (one per substrate/species/pre-drilling
 * combination), so it's edited as an add/remove sub-table rather than forced into a select control.
 */
const ResistanceValuesEditor: FC<ResistanceValuesEditorProps> = ({ value, onChange }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;

  const resistanceParamLabels: Record<ResistanceParam, string> = {
    fax_k: t.resistanceValues.params.fax_k,
    fhead_k: t.resistanceValues.params.fhead_k,
  };

  const substrateCategoryLabels: Record<SubstrateCategory, string> = t.substrateCategories;

  const updateRow = (index: number, patch: Partial<ResistanceValue>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Table>
        <thead>
          <TableRow>
            <TableHeadCell align="left">{t.resistanceValues.fields.resistanceParam}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.substrateCategory}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.substrateSpecies}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.preDrilled}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.valueNMm2}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.referenceDensity}</TableHeadCell>
            <TableHeadCell align="left">{t.resistanceValues.fields.densityRange}</TableHeadCell>
            <TableHeadCell align="left"></TableHeadCell>
          </TableRow>
        </thead>
        <tbody>
          {value.map((row, index) => (
            <TableRow key={index}>
              <TableCell>
                <Select<{ value: ResistanceParam; label: string }, false>
                  options={RESISTANCE_PARAMS.map((v) => ({ value: v, label: resistanceParamLabels[v] }))}
                  value={{ value: row.resistanceParam, label: resistanceParamLabels[row.resistanceParam] }}
                  getOptionLabel={(o) => o.label}
                  getOptionValue={(o) => o.value}
                  onChange={(o) => o && updateRow(index, { resistanceParam: o.value })}
                />
              </TableCell>
              <TableCell>
                <Select<{ value: SubstrateCategory; label: string }, false>
                  options={SUBSTRATE_CATEGORIES.map((v) => ({ value: v, label: substrateCategoryLabels[v] }))}
                  value={{ value: row.substrateCategory, label: substrateCategoryLabels[row.substrateCategory] }}
                  getOptionLabel={(o) => o.label}
                  getOptionValue={(o) => o.value}
                  onChange={(o) => o && updateRow(index, { substrateCategory: o.value })}
                />
              </TableCell>
              <TableCell>
                <Input value={row.substrateSpecies} onChange={(e) => updateRow(index, { substrateSpecies: e.target.value })} />
              </TableCell>
              <TableCell>
                <Switch
                  id={`resistance-pre-drilled-${index}`}
                  checked={row.preDrilled}
                  onChange={(checked) => updateRow(index, { preDrilled: checked })}
                />
              </TableCell>
              <TableCell>
                <Numeric
                  value={row.valueNMm2 ?? undefined}
                  decimalScale={3}
                  onValueChange={(v) => updateRow(index, { valueNMm2: v ?? null })}
                />
              </TableCell>
              <TableCell>
                <Numeric
                  value={row.referenceDensity ?? undefined}
                  decimalScale={1}
                  onValueChange={(v) => updateRow(index, { referenceDensity: v ?? null })}
                />
              </TableCell>
              <TableCell>
                <Input
                  value={row.densityRange ?? ""}
                  onChange={(e) => updateRow(index, { densityRange: e.target.value || null })}
                />
              </TableCell>
              <TableCell>
                <Button variant="secondary" onClick={() => removeRow(index)}>
                  {i18n.translation.common.delete}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
      <Button variant="secondary" onClick={() => onChange([...value, emptyRow()])}>
        <Icon glyph={AddIcon} />
        {t.resistanceValues.addRow}
      </Button>
    </div>
  );
};

export default ResistanceValuesEditor;
